import type { BillingSummary } from '../../../types/billing.types';
import type { BoardConnection } from '../../../types/board.types';

/** What a connection card says about the plan, when there is something to say. */
export type ConnectionBillingState =
  /** The plan doesn't cover this source any more, so nothing syncs. */
  | { kind: 'paused' }
  /** A professional source on a free trial. */
  | { kind: 'trial'; endsAt: string; daysLeft: number; elapsedFraction: number };

const DAY_MS = 24 * 60 * 60 * 1000;

const sameId = (left: string, right: string) => left.toLowerCase() === right.toLowerCase();

/**
 * The plan state for a connection card, or null when there is nothing to say: the source is covered, billing is
 * off for the user, or limits aren't enforced for them. Display only; the server decides what is paused.
 * `elapsedFraction` is how much of the trial has gone, 0 to 1, for a progress bar that fills as time runs out.
 */
export function getConnectionBillingState(
  connection: BoardConnection,
  billing: BillingSummary | undefined,
  now: Date,
): ConnectionBillingState | null {
  if (!billing?.billingEnabled || !billing.enforcementEnabled || connection.archived) return null;

  if (connection.planPausedAt) return { kind: 'paused' };

  const trial = billing.sourceTrials?.find((candidate) => sameId(candidate.sourceTypeId, connection.sourceTypeId));
  if (!trial?.isActive) return null;

  const startedAt = new Date(trial.startedAt).getTime();
  const endsAt = new Date(trial.endsAt).getTime();
  const length = endsAt - startedAt;

  return {
    kind: 'trial',
    endsAt: trial.endsAt,
    daysLeft: Math.max(0, Math.ceil((endsAt - now.getTime()) / DAY_MS)),
    elapsedFraction: length > 0 ? Math.min(1, Math.max(0, (now.getTime() - startedAt) / length)) : 1,
  };
}
